import React, { useState, useRef, useEffect } from 'react';
import { Button, Input, Textarea, Chip } from '@nextui-org/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faEdit, 
  faSave, 
  faTimes, 
  faPlus, 
  faTrash, 
  faChevronDown, 
  faChevronRight,
  faGripVertical
} from '@fortawesome/free-solid-svg-icons';
import { StandardSaveButton, StandardCancelButton, StandardSecondaryButton, StandardDeleteButton, StandardAddButton } from "../StandardButtons";

// XSS Protection and Input Validation
const sanitizeInput = (input) => {
  if (!input) return '';
  return input
    .replace(/[<>]/g, '') // Remove < and >
    .replace(/javascript:/gi, '') // Remove javascript: protocol
    .replace(/on\w+=/gi, '') // Remove event handlers
    .trim();
};

const validateSectionName = (name) => {
  if (!name || name.length < 2 || name.length > 100) return false;
  const pattern = /^[A-Za-z0-9\s\-_\.]+$/;
  return pattern.test(name);
};

const validateSectionCode = (code) => {
  if (!code) return true; // Code is optional
  if (code.length > 20) return false;
  const pattern = /^[A-Za-z0-9\-_\.]+$/;
  return pattern.test(code);
};

const EditableSection = ({ 
  standard, 
  level = 0, 
  onUpdate,
  onSectionUpdate, // Handle both prop names
  onAddChild, 
  onDelete,
  onDeleteSection, // Handle both prop names
  onReorder,
  isEditing = false,
  onEditToggle,
  index = 0,
  totalSiblings = 1,

  availableMetrics,
  isLoading,
  onSave,
  onReset,
  onStopEditing,
  hasUnsavedChanges
}) => {

  const [isEditingName, setIsEditingName] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [isEditingCode, setIsEditingCode] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  
  const [editName, setEditName] = useState(standard?.name || '');
  const [editDescription, setEditDescription] = useState(standard?.description || '');
  const [editCode, setEditCode] = useState(standard?.code || '');
  
  const [nameError, setNameError] = useState('');
  const [codeError, setCodeError] = useState('');
  
  const nameInputRef = useRef(null);
  const descriptionInputRef = useRef(null);
  const codeInputRef = useRef(null);

  // Normalize prop names
  const handleUpdate = onUpdate || onSectionUpdate;
  const handleDelete = onDelete || onDeleteSection;

  // Add validation to ensure standard exists
  if (!standard) {
    console.warn('EditableSection: standard prop is undefined or null');
    return (
      <div className="p-4 border border-red-200 rounded-lg bg-red-50">
        <p className="text-red-600 text-sm">Error: Standard data is missing</p>
      </div>
    );
  }

  // Validate required props
  if (!handleUpdate) {
    console.error('EditableSection: onUpdate or onSectionUpdate prop is required');
    return (
      <div className="p-4 border border-red-200 rounded-lg bg-red-50">
        <p className="text-red-600 text-sm">Error: Update handler is missing</p>
      </div>
    );
  }

  if (!onAddChild) {
    console.error('EditableSection: onAddChild prop is required');
    return (
      <div className="p-4 border border-red-200 rounded-lg bg-red-50">
        <p className="text-red-600 text-sm">Error: Add child handler is missing</p>
      </div>
    );
  }

  if (!handleDelete) {
    console.error('EditableSection: onDelete or onDeleteSection prop is required');
    return (
      <div className="p-4 border border-red-200 rounded-lg bg-red-50">
        <p className="text-red-600 text-sm">Error: Delete handler is missing</p>
      </div>
    );
  }

  const handleSaveName = () => {
    const sanitizedName = sanitizeInput(editName);
    if (!validateSectionName(sanitizedName)) {
      setNameError('Name must be 2-100 characters and contain only letters, numbers, spaces, hyphens, underscores, and periods');
      return;
    }
    
    if (sanitizedName !== standard.name) {
      handleUpdate(standard.id, { ...standard, name: sanitizedName });
    }
    setIsEditingName(false);
    setNameError('');
  };

  const handleSaveDescription = () => {
    const sanitizedDescription = sanitizeInput(editDescription);
    if (sanitizedDescription !== standard.description) {
      handleUpdate(standard.id, { ...standard, description: sanitizedDescription });
    }
    setIsEditingDescription(false);
  };

  const handleSaveCode = () => {
    const sanitizedCode = sanitizeInput(editCode);
    if (sanitizedCode && !validateSectionCode(sanitizedCode)) {
      setCodeError('Code must be 1-20 characters and contain only letters, numbers, hyphens, underscores, and periods');
      return;
    }
    
    if (sanitizedCode !== standard.code) {
      handleUpdate(standard.id, { ...standard, code: sanitizedCode });
    }
    setIsEditingCode(false);
    setCodeError('');
  };

  const handleCancelEdit = (field) => {
    switch (field) {
      case 'name':
        setEditName(standard.name || '');
        setIsEditingName(false);
        setNameError('');
        break;
      case 'description':
        setEditDescription(standard.description || '');
        setIsEditingDescription(false);
        break;
      case 'code':
        setEditCode(standard.code || '');
        setIsEditingCode(false);
        setCodeError('');
        break;
    }
  };

  const handleAddChild = () => {
    const newChild = {
      id: `new_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      name: 'New Section',
      description: '',
      code: '',
      level: level + 1,
      children: []
    };
    onAddChild(standard.id, newChild);
  };

  const confirmDelete = () => {
    if (window.confirm(`Are you sure you want to delete "${standard.name}"?`)) {
      handleDelete(standard.id);
    }
  };

  const handleDragStart = (e) => {
    setIsDragging(true);
    e.dataTransfer.setData('text/plain', standard.id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const draggedId = e.dataTransfer.getData('text/plain');
    if (draggedId !== standard.id) {
      // Handle reordering logic here
    }
  };

  const indentStyle = {
    marginLeft: `${level * 20}px`,
    borderLeft: level > 0 ? '2px solid #10b981' : 'none',
    paddingLeft: level > 0 ? '16px' : '0'
  };

  return (
    <div 
      className={`border rounded-lg p-5 mb-4 transition-all duration-200 ${
        isDragging ? 'opacity-50 bg-emerald-50' : 'bg-white hover:shadow-sm'
      } ${level === 0 ? 'border-emerald-200 shadow-md' : 'border-slate-200'}`}
      style={indentStyle}
      draggable={isEditing}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Section Header */}
      <div className="flex items-center gap-4 mb-4">
        {/* Drag Handle */}
        {isEditing && (
          <div className="cursor-grab text-slate-400 hover:text-emerald-600 transition-colors duration-200">
            <FontAwesomeIcon icon={faGripVertical} className="w-4 h-4" />
          </div>
        )}
        
        {/* Expand/Collapse */}
        {standard.children && standard.children.length > 0 && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-slate-500 hover:text-emerald-700 transition-colors duration-200 p-1 rounded"
          >
            <FontAwesomeIcon 
              icon={isExpanded ? faChevronDown : faChevronRight} 
              className="w-4 h-4" 
            />
          </button>
        )}
        
        {/* Section Level Indicator */}
        <Chip 
          size="sm" 
          variant="flat" 
          color={level === 0 ? "success" : "default"} 
          className={`text-xs font-medium ${
            level === 0 ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-700'
          }`}
        >
          L{level}
        </Chip>
        
        {/* Section Name */}
        <div className="flex-1">
          {isEditingName ? (
            <div className="flex items-center gap-2">
              <Input
                ref={nameInputRef}
                size="sm"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSaveName()}
                onKeyDown={(e) => e.key === 'Escape' && handleCancelEdit('name')}
                isInvalid={!!nameError}
                errorMessage={nameError}
                className="flex-1"
              />
              <StandardSaveButton size="sm" onClick={handleSaveName} className="min-w-0 px-2">
                <FontAwesomeIcon icon={faSave} className="w-3 h-3" />
              </StandardSaveButton>
              <StandardCancelButton size="sm" onClick={() => handleCancelEdit('name')} className="min-w-0 px-2">
                <FontAwesomeIcon icon={faTimes} className="w-3 h-3" />
              </StandardCancelButton>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <h5 className={`font-semibold text-slate-900 ${level === 0 ? 'text-lg' : 'text-base'} leading-tight`}>
                {standard.name || 'Unnamed Section'}
              </h5>
              {isEditing && (
                <StandardSecondaryButton
                  size="sm"
                  onClick={() => setIsEditingName(true)}
                  className="min-w-0 p-1"
                >
                  <FontAwesomeIcon icon={faEdit} className="w-3 h-3" />
                </StandardSecondaryButton>
              )}
            </div>
          )}
        </div>
        
        {/* Section Code */}
        {isEditingCode ? (
          <div className="flex items-center gap-2">
            <Input
              ref={codeInputRef}
              size="sm"
              placeholder="Code"
              value={editCode}
              onChange={(e) => setEditCode(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSaveCode()}
              onKeyDown={(e) => e.key === 'Escape' && handleCancelEdit('code')}
              isInvalid={!!codeError}
              errorMessage={codeError}
              className="w-24"
            />
            <StandardSaveButton size="sm" onClick={handleSaveCode} className="min-w-0 px-2">
              <FontAwesomeIcon icon={faSave} className="w-3 h-3" />
            </StandardSaveButton>
            <StandardCancelButton size="sm" onClick={() => handleCancelEdit('code')} className="min-w-0 px-2">
              <FontAwesomeIcon icon={faTimes} className="w-3 h-3" />
            </StandardCancelButton>
          </div>
        ) : (
          standard.code && (
            <div className="flex items-center gap-2">
              <Chip size="sm" variant="flat" color="primary" className="text-xs bg-emerald-100 text-emerald-800">
                {standard.code}
              </Chip>
              {isEditing && (
                <StandardSecondaryButton
                  size="sm"
                  onClick={() => setIsEditingCode(true)}
                  className="min-w-0 p-1"
                >
                  <FontAwesomeIcon icon={faEdit} className="w-3 h-3" />
                </StandardSecondaryButton>
              )}
            </div>
          )
        )}
        
        {/* Action Buttons */}
        {isEditing && (
          <div className="flex items-center gap-2">
            <StandardAddButton
              size="sm"
              onClick={handleAddChild}
              className="min-w-0 p-2"
            >
              <FontAwesomeIcon icon={faPlus} className="w-3 h-3" />
            </StandardAddButton>
            <StandardDeleteButton
              size="sm"
              onClick={confirmDelete}
              className="min-w-0 p-2"
            >
              <FontAwesomeIcon icon={faTrash} className="w-3 h-3" />
            </StandardDeleteButton>
          </div>
        )}
      </div>
      
      {/* Section Description */}
      <div className="mb-4">
        {isEditingDescription ? (
          <div className="flex items-start gap-2">
            <Textarea
              ref={descriptionInputRef}
              size="sm"
              placeholder="Description"
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              onKeyDown={(e) => e.key === 'Escape' && handleCancelEdit('description')}
              className="flex-1"
              minRows={1}
              maxRows={3}
            />
            <div className="flex flex-col gap-1">
              <StandardSaveButton size="sm" onClick={handleSaveDescription} className="min-w-0 px-2">
                <FontAwesomeIcon icon={faSave} className="w-3 h-3" />
              </StandardSaveButton>
              <Button size="sm" variant="bordered" onClick={() => handleCancelEdit('description')} className="min-w-0 px-2">
                <FontAwesomeIcon icon={faTimes} className="w-3 h-3" />
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex items-start gap-2">
            {standard.description ? (
              <p className="text-sm text-slate-600 flex-1 leading-relaxed">
                {standard.description}
              </p>
            ) : (
              <p className="text-sm text-slate-400 flex-1 italic">
                No description
              </p>
            )}
            {isEditing && (
              <Button
                size="sm"
                variant="light"
                onClick={() => setIsEditingDescription(true)}
                className="min-w-0 p-1 text-slate-600 hover:text-emerald-700"
              >
                <FontAwesomeIcon icon={faEdit} className="w-3 h-3" />
              </Button>
            )}
          </div>
        )}
      </div>
      
      {/* Children Sections */}
      {isExpanded && standard.children && standard.children.length > 0 && (
        <div className="mt-5 space-y-4">
          {standard.children.map((child, childIndex) => (
            <EditableSection
              key={child.id}
              standard={child}
              level={level + 1}
              onUpdate={onUpdate}
              onSectionUpdate={onSectionUpdate}
              onAddChild={onAddChild}
              onDelete={onDelete}
              onDeleteSection={onDeleteSection}
              onReorder={onReorder}
              isEditing={isEditing}
              onEditToggle={onEditToggle}
              index={childIndex}
              totalSiblings={standard.children.length}
              // Pass additional props down
              availableMetrics={availableMetrics}
              isLoading={isLoading}
              onSave={onSave}
              onReset={onReset}
              onStopEditing={onStopEditing}
              hasUnsavedChanges={hasUnsavedChanges}
            />
          ))}
        </div>
      )}
      
      {/* Empty State for Sections with No Children */}
      {isExpanded && (!standard.children || standard.children.length === 0) && (
        <div className="text-center py-4 text-sm text-slate-500 border-2 border-dashed border-slate-300 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors">
          No subsections
        </div>
      )}
    </div>
  );
};

export default EditableSection;
